import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
	postAnswer,
	postReply,
	postInaccurateAnswer,
	postUpVoteAnswer,
	postUpVoteQuestion,
	postQuestion,
	getProductQuestions,
	postRegisterUser
} from './api';

const SORT_MOST_HELPFUL = 1;
const SORT_MOST_RECENT = 2;

const reducerName = 'productQuestionsAndAnswers';
const initialState = {
	totalQuestions: 0,
	sortOrder: 1,
	fetchStatus: '',
	pageNumber: 0,
	pageCount: 0,
	pageSize: 0,
	totalRecords: 0,
	questions: [],
	createQuestionStatus: '',
	newQuestionModeEnabled: false,
	questionText: '',
	createReplyStatus: ''
};

const fetchProductQuestions = createAsyncThunk(
	reducerName,
	async (apiParams) => {

		const response = await getProductQuestions({ ...apiParams });

		return response;

	},
	{
		condition: (arg, { getState }) => {

			const { productQuestionsAndAnswers } = getState();
			const { fetchStatus } = productQuestionsAndAnswers;
			if (fetchStatus === 'pending') {

				return false;

			}
			return true;

		}
	}
);

const addAnswer = createAsyncThunk(`${reducerName}/addAnswer`, async (data) => {

	const response = await postAnswer(data);

	return response.result;

});

const addReply = createAsyncThunk(`${reducerName}/addReply`, async (data) => {

	const response = await postReply(data);

	return response.result;

});

const updateInaccurateAnswer = createAsyncThunk(`${reducerName}/updateInaccurateAnswer`, async (id) => {

	const response = await postInaccurateAnswer(id);

	return response;

});

const updateUpVoteAnswer = createAsyncThunk(`${reducerName}/updateUpVoteAnswer`, async (id) => {

	const response = await postUpVoteAnswer(id);

	return response;

});
const updateUpVoteQuestion = createAsyncThunk(`${reducerName}/updateUpVoteQuestion`, async (id) => {

	const response = await postUpVoteQuestion(id);

	return response;

});
const addQuestion = createAsyncThunk(`${reducerName}/addQuestion`, async (questionPayload) => {

	const response = await postQuestion(questionPayload);

	return response;

});

const registerUserEmail = createAsyncThunk(`${reducerName}/registerUserEmail`, async (data) => {

	const response = await postRegisterUser(data);

	return response;

});

const updateQuestionsAnswersWithProp = (questionsArray, updateQuestionId, updateAnswerId, prop, value) => questionsArray.map((question) => {

	if (question.id === updateQuestionId) {

		const updateAnswerIndex = question.answers.findIndex((answer) => answer.id === updateAnswerId);
		if (updateAnswerIndex > -1) {

			return {
				...question,
				answers: [
					...question.answers.slice(0, updateAnswerIndex),
					{
						...question.answers[updateAnswerIndex],
						[prop]: value
					},
					...question.answers.slice(updateAnswerIndex + 1)
				]
			};

		}
		return question;

	}

	return question;

});

const productQuestionsAndAnswersSlice = createSlice({
	name: reducerName,
	initialState,
	reducers: {
		updateSortOrder (state, action) {

			state.sortOrder = action.payload;

		},
		enableNewQuestionMode (state) {

			state.newQuestionModeEnabled = true;
			state.createQuestionStatus = '';

		},
		disableNewQuestionMode (state) {

			state.newQuestionModeEnabled = false;
			state.createQuestionStatus = '';

		},
		setQuestionText (state, action) {

			state.questionText = action.payload;

		},
		resetState (state, action) {

			const questions = action.payload.questionsData || null;

			return {
				totalQuestions: questions?.results?.length || 0,
				sortOrder: 1,
				fetchStatus: 'fulfilled',
				pageNumber: questions?.pageNumber ?? 0,
				pageCount: questions?.pageCount ?? 0,
				pageSize: questions?.pageSize ?? 0,
				totalRecords: questions?.totalRecords ?? 0,
				questions: questions?.results ?? [],
				createQuestionStatus: '',
				newQuestionModeEnabled: false,
				questionText: '',
				createReplyStatus: ''
			};

		}
	},
	extraReducers: {
		[fetchProductQuestions.pending]: (state) => {

			state.fetchStatus = 'pending';

		},
		[fetchProductQuestions.fulfilled]: (state, action) => {

			const { payload } = action;
			const { pageCount, pageNumber, results } = payload.result;

			state.pageCount = pageCount;
			state.pageNumber = pageNumber;
			state.totalQuestions = results.length;
			state.questions = results;

			state.fetchStatus = 'fulfilled';

		},
		[fetchProductQuestions.rejected]: (state, action) => {

			state.fetchProductQuestionsError = action.payload;
			state.fetchStatus = 'rejected';

		},
		[addReply.fulfilled]: (state) => {

			state.createReplyStatus = 'fulfilled';

		},
		[addReply.pending]: (state) => {

			state.createReplyStatus = 'pending';

		},
		[addReply.rejected]: (state) => {

			state.createReplyStatus = 'rejected';

		},
		[updateInaccurateAnswer.fulfilled]: (state, action) => {

			const updateQuestionId = action.payload?.result?.questionId;
			const updateAnswerId = action.payload?.result?.id;
			state.questions = updateQuestionsAnswersWithProp(
				state.questions,
				updateQuestionId,
				updateAnswerId,
				'flagged',
				true
			);

		},
		[updateUpVoteAnswer.fulfilled]: (state, action) => {

			const updateQuestionId = action.payload?.result?.questionId;
			const updateAnswerId = action.payload?.result?.id;
			state.questions = updateQuestionsAnswersWithProp(
				state.questions,
				updateQuestionId,
				updateAnswerId,
				'upvoted',
				true
			);

		},
		[updateUpVoteQuestion.fulfilled]: () => {},
		[addQuestion.fulfilled]: (state, action) => {

			state.questions = [ action.payload, ...state.questions ];
			state.createQuestionStatus = 'fulfilled';

		},
		[addQuestion.pending]: (state) => {

			state.createQuestionStatus = 'pending';

		},
		[addQuestion.rejected]: (state) => {

			state.createQuestionStatus = 'rejected';

		},
		[registerUserEmail.rejected]: () => {

			console.error('Unable to register user');

		}
	}
});

const {
	updateSortOrder, enableNewQuestionMode, disableNewQuestionMode, setQuestionText, resetState
} =	productQuestionsAndAnswersSlice.actions;

export {
	SORT_MOST_HELPFUL,
	SORT_MOST_RECENT,
	fetchProductQuestions,
	updateSortOrder,
	updateInaccurateAnswer,
	addAnswer,
	addReply,
	updateUpVoteAnswer,
	updateUpVoteQuestion,
	enableNewQuestionMode,
	disableNewQuestionMode,
	addQuestion,
	setQuestionText,
	registerUserEmail,
	resetState
};

export default productQuestionsAndAnswersSlice.reducer;
